import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination from '../components/Pagination';
import { useToast } from '../components/Toast';
import {
  Agent,
  deleteAgent,
  formatJoinedDate,
  listAgents,
  setAgentApproval,
} from '../api/agents';
import { extractApiError } from '../api/legalServices';

type StatusFilter = 'all' | 'active' | 'pending';
const PAGE_LIMIT = 20;

const Agents: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [busyId, setBusyId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Agent | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Debounce search input -> committed search by 350ms
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listAgents({
        search: search || undefined,
        isApproved:
          statusFilter === 'active'
            ? true
            : statusFilter === 'pending'
            ? false
            : undefined,
        page,
        limit: PAGE_LIMIT,
      });
      setAgents(res.agents);
      setTotal(res.total);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load merchants'));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const activeCount = useMemo(
    () => agents.filter((a) => a.isApproved).length,
    [agents]
  );
  const pendingCount = useMemo(
    () => agents.filter((a) => !a.isApproved).length,
    [agents]
  );

  const handleApprovalToggle = async (a: Agent, desired: boolean) => {
    setBusyId(a.id);
    setAgents((prev) =>
      prev.map((x) => (x.id === a.id ? { ...x, isApproved: desired } : x))
    );
    try {
      const updated = await setAgentApproval(a.id, desired);
      setAgents((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
      toast(
        updated.name + ' ' + (updated.isApproved ? 'approved' : 'suspended'),
        updated.isApproved ? 'success' : 'info'
      );
    } catch (err) {
      // revert
      setAgents((prev) =>
        prev.map((x) =>
          x.id === a.id ? { ...x, isApproved: a.isApproved } : x
        )
      );
      toast(extractApiError(err, 'Failed to update merchant'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const requestDelete = (a: Agent) => setPendingDelete(a);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const a = pendingDelete;
    setDeleting(true);
    try {
      await deleteAgent(a.id);
      setAgents((prev) => prev.filter((x) => x.id !== a.id));
      setTotal((t) => Math.max(0, t - 1));
      toast(a.name + ' deleted', 'info');
      setPendingDelete(null);
      // If page now empty and not first page, step back
      if (agents.length === 1 && page > 1) setPage(page - 1);
    } catch (err) {
      toast(extractApiError(err, 'Delete failed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Stats row */}
      <div className="flex gap-4 flex-wrap mb-6">
        <div
          className="card flex-1 min-w-[200px] p-5 animate-fadeIn"
          style={{ animationDelay: '0ms' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--brand-bg)' }}
            >
              <Icon name="users" size={20} color="var(--brand)" />
            </div>
            <div className="text-[13px] text-g500 font-medium">
              Total on this page
            </div>
          </div>
          <div className="text-[26px] font-extrabold text-ink leading-tight">
            {agents.length}
          </div>
          <div className="text-[12px] text-g400 mt-1">{total} total merchants</div>
        </div>
        <div
          className="card flex-1 min-w-[200px] p-5 animate-fadeIn"
          style={{ animationDelay: '80ms' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--green-bg)' }}
            >
              <Icon name="user-check" size={20} color="var(--green)" />
            </div>
            <div className="text-[13px] text-g500 font-medium">Approved</div>
          </div>
          <div className="text-[26px] font-extrabold text-ink leading-tight">
            {activeCount}
          </div>
          <div className="text-[12px] text-g400 mt-1">on this page</div>
        </div>
        <div
          className="card flex-1 min-w-[200px] p-5 animate-fadeIn"
          style={{ animationDelay: '160ms' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--yellow-bg)' }}
            >
              <Icon name="bell" size={20} color="var(--yellow)" />
            </div>
            <div className="text-[13px] text-g500 font-medium">Pending</div>
          </div>
          <div className="text-[26px] font-extrabold text-ink leading-tight">
            {pendingCount}
          </div>
          <div className="text-[12px] text-g400 mt-1">awaiting approval</div>
        </div>
      </div>

      {/* Search + filter row */}
      <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
        <div className="relative flex-1" style={{ maxWidth: 420 }}>
          <span className="absolute left-3 top-1/2 -translate-y-1/2">
            <Icon name="search" size={18} color="var(--g400)" />
          </span>
          <input
            className="form-input"
            style={{ paddingLeft: 40 }}
            placeholder="Search merchants by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {(['all', 'active', 'pending'] as StatusFilter[]).map((f) => {
            const active = statusFilter === f;
            const label = f === 'active' ? 'Approved' : f;
            return (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className="capitalize"
                style={{
                  padding: '8px 16px',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  border:
                    '1px solid ' +
                    (active ? 'var(--brand)' : 'var(--g200)'),
                  background: active ? 'var(--brand-light)' : '#fff',
                  color: active ? 'var(--brand)' : 'var(--g600)',
                  cursor: 'pointer',
                  transition: 'all .15s ease',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Load error */}
      {loadError && (
        <div
          className="card p-5 mb-5"
          role="alert"
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load merchants</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {/* Table */}
      <div
        className="bg-white rounded-card overflow-hidden border border-g100"
        style={{
          boxShadow:
            '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {['Merchant', 'Email', 'Joined', 'Status', 'Actions'].map((h, i) => (
                <th
                  key={h}
                  className={
                    'px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap' +
                    (i === 4 ? ' text-right' : '')
                  }
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5}>
                  <div className="py-12 text-center text-g500">
                    <Icon
                      name="users"
                      size={28}
                      color="var(--g400)"
                    />{' '}
                    Loading merchants...
                  </div>
                </td>
              </tr>
            ) : agents.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div
                      className="rounded-[20px] flex items-center justify-center mb-5"
                      style={{
                        width: 72,
                        height: 72,
                        background: 'var(--g100)',
                      }}
                    >
                      <Icon name="users" size={32} color="var(--g400)" />
                    </div>
                    <div className="text-[17px] font-semibold text-ink mb-1.5">
                      No merchants found
                    </div>
                    <div
                      className="text-sm text-g500"
                      style={{ maxWidth: 340 }}
                    >
                      {search || statusFilter !== 'all'
                        ? 'Try adjusting your search or filter.'
                        : 'No merchants have registered yet.'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              agents.map((a) => {
                const isBusy = busyId === a.id;
                return (
                  <tr
                    key={a.id}
                    className="hover:bg-g50 transition-colors"
                    style={{ opacity: isBusy ? 0.7 : 1 }}
                  >
                    <td className="px-4 py-3.5 whitespace-nowrap border-b border-g100">
                      <div className="flex items-center gap-3">
                        <Avatar src={a.avatar} name={a.name || a.email} id={a.id} size={36} />
                        <div
                          className="cursor-pointer"
                          onClick={() => navigate(`/admin/agents/${a.id}`)}
                        >
                          <div className="font-semibold text-sm hover:text-brand">
                            {a.name || (
                              <span className="text-g400 italic">
                                Unnamed
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-g500">
                            ID #{a.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span className="flex items-center gap-2">
                        <Icon
                          name="mail"
                          size={14}
                          color="var(--g400)"
                        />
                        {a.email}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-g500 whitespace-nowrap border-b border-g100">
                      {formatJoinedDate(a.createdAt)}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span
                        className={
                          'badge ' +
                          (a.isApproved ? 'badge-active' : 'badge-pending')
                        }
                      >
                        {a.isApproved ? 'Approved' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100 text-right">
                      <div className="flex gap-1.5 justify-end">
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => navigate(`/admin/agents/${a.id}`)}
                          title="View details"
                        >
                          <Icon name="eye" size={14} /> View
                        </button>
                        {!a.isApproved ? (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleApprovalToggle(a, true)}
                            disabled={isBusy}
                          >
                            <Icon
                              name="check-circle"
                              size={14}
                              color="#fff"
                            />{' '}
                            Approve
                          </button>
                        ) : (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleApprovalToggle(a, false)}
                            disabled={isBusy}
                          >
                            <Icon name="x-circle" size={14} /> Suspend
                          </button>
                        )}
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ color: 'var(--red)' }}
                          onClick={() => requestDelete(a)}
                          disabled={isBusy}
                          aria-label="Delete merchant"
                        >
                          <Icon
                            name="x-circle"
                            size={14}
                            color="var(--red)"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={PAGE_LIMIT} total={total} onChange={setPage} disabled={loading} />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete Merchant"
        variant="danger"
        confirmLabel="Delete"
        cancelLabel="Keep"
        busy={deleting}
        message={
          <>
            Are you sure you want to permanently delete{' '}
            <strong style={{ color: 'var(--ink)' }}>
              {pendingDelete?.name || pendingDelete?.email}
            </strong>
            ?
            <br />
            Their listings and data will become orphaned. This cannot be
            undone.
          </>
        }
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </div>
  );
};

export default Agents;
